import { Navigate, Route, Routes } from 'react-router-dom';
import { lazy, Suspense, useContext } from 'react';
import { UserContext } from '@context/createContext';

import Login from '@modules/realestateLogin/login/Login.jsx';
import Layout from '@layout/Layout.jsx';
import Loader from '@modules/loading/Loader';

const Dashboard = lazy(() => import('@pages/Dashboard.jsx'));
const Agents = lazy(() => import('@pages/Agents.jsx'));
const AdsListWrapper = lazy(() => import('@/pages/AdsListWrapper.jsx'));
const AdsDetails = lazy(() => import('@pages/AdsDetails.jsx'));
const AgentsDetails = lazy(() => import('@pages/AgentsDetails.jsx'));
const AdsCreate = lazy(() => import('@pages/AdsCreate.jsx'));
const AdsEdit = lazy(() => import('@pages/AdsEdit.jsx'));
const Subscription = lazy(() => import('@pages/Subscription.jsx'));
const Settings = lazy(() => import('@pages/Settings.jsx'));
const Requests = lazy(() => import('@pages/Requests.jsx'));
const Customers = lazy(() => import('@pages/Customers.jsx'));
const FailureBilling = lazy(() => import('@pages/FailureBilling'));
const SuccessBilling = lazy(() => import('@pages/SuccessBilling'));
const SubscriptionAgent = lazy(() => import('@pages/SubscriptionAgent'));
const AgentsHandler = lazy(() => import('@pages/AgentsHandler'));
// const HelpCenter = lazy(() => import("@pages/HelpCenter"));
// const HelpCenterSection = lazy(() => import("@pages/HelpCenterSection"));

import { Bounce, ToastContainer } from 'react-toastify';
import 'react-loading-skeleton/dist/skeleton.css';

// import "react-toastify/dist/ReactToastify.css";

function App() {
  const { user, isLoading, realEstate } = useContext(UserContext);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen w-screen">
        <Loader />
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route
          path="/login"
          element={
            <>
              <ToastContainer
                position="top-center"
                autoClose={3000}
                hideProgressBar={true}
                closeOnClick={true}
                rtl={false}
                draggable
                transition={Bounce}
                stacked={window.innerWidth > 768 ? true : false}
                icon={false}
                closeButton={false}
                style={{
                  top: '16px', // فاصله از بالا
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '90%', // برای موبایل بهتر
                  // maxWidth: "300px", // جلوگیری از بزرگ‌تر شدن از این مقدار
                }}
              />
              <Login />
            </>
          }
        />
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    );
  }

  return (
    <Suspense
      fallback={
        <div className="flex flex-col items-center justify-center w-screen h-screen">
          <Loader />
        </div>
      }
    >
      <Routes>
        <Route path="/" element={<Layout />}>
          {/* <Route path="help" element={<HelpCenter />} /> */}
          {/* <Route path="help/:name" element={<HelpCenterSection />} /> */}
          {getRoutesBasedOnUserAndSubscription(user, realEstate)}
        </Route>
      </Routes>
    </Suspense>
  );
}

function getRoutesBasedOnUserAndSubscription(user, realEstate) {
  if (!realEstate?.subscriptionInfo?.isAgentsExceeded) {
    if (user.realState) {
      if (realEstate?.subscriptionInfo.remainingDays > 0) {
        return user.userType === 'realStateOwner'
          ? getOwnerRoutesWithActiveSubscription()
          : user.userType === 'agent'
            ? getAgentRoutesWithActiveSubscription()
            : null;
      } else {
        return user.userType === 'realStateOwner'
          ? getOwnerRoutesWithExpiredSubscription()
          : user.userType === 'agent'
            ? getAgentRoutesWithExpiredSubscription()
            : null;
      }
    } else {
      return getAgentRoutesWithoutRealstate();
    }
  } else {
    return user.userType === 'realStateOwner'
      ? getOwnerRoutesWithExceededAgentsLimit()
      : user.userType === 'agent'
        ? getAgentsExceededAgentRoutes()
        : null;
  }
}

// کاربر owner با realstate, subscription
function getOwnerRoutesWithActiveSubscription() {
  return (
    <>
      <Route path="*" element={<Navigate to="/" />} />
      <Route path="/" element={<Dashboard />} />
      <Route path="ads/list" element={<AdsListWrapper />} />
      <Route path="ads/list/:id" element={<AdsDetails />} />
      <Route path="ads/create" element={<AdsCreate />} />
      <Route path="ads/edit/:id" element={<AdsEdit />} />
      <Route path="customers" element={<Customers />} />
      <Route path="agents/invations" element={<Requests />} />
      <Route path="settings" element={<Settings />} />
      <Route path="agents/list" element={<Agents />} />
      <Route path="agents/list/:id" element={<AgentsDetails />} />
      <Route path="subscription" element={<Subscription />} />
      <Route path="billing/invoiceorder/failure" element={<FailureBilling />} />
      <Route path="billing/invoiceorder/success" element={<SuccessBilling />} />
    </>
  );
}

// کاربر agent با realstate, subscription
function getAgentRoutesWithActiveSubscription() {
  return (
    <>
      <Route path="*" element={<Navigate to="/" />} />
      <Route path="/" element={<Dashboard />} />
      <Route path="ads/list" element={<AdsListWrapper />} />
      <Route path="ads/list/:id" element={<AdsDetails />} />
      <Route path="ads/create" element={<AdsCreate />} />
      <Route path="ads/edit/:id" element={<AdsEdit />} />
      <Route path="customers" element={<Customers />} />
      <Route path="requests" element={<Requests />} />
      <Route path="settings" element={<Settings />} />
    </>
  );
}

// کاربر owner با محدودیت در اشتراک
function getOwnerRoutesWithExceededAgentsLimit() {
  return (
    <>
      <Route path="/" element={<AgentsHandler />} />
      <Route path="*" element={<Navigate to="/" />} />
    </>
  );
}

// کاربر agent بدون realstate
function getAgentRoutesWithoutRealstate() {
  return (
    <>
      <Route path="requests" element={<Requests />} />
      <Route path="/" element={<Navigate to="/requests" />} />
      <Route path="*" element={<Navigate to="/requests" />} />
    </>
  );
}

// کاربر owner بدون subscription
function getOwnerRoutesWithExpiredSubscription() {
  return (
    <>
      <Route path="subscription" element={<Subscription />} />
      <Route path="billing/invoiceorder/failure" element={<FailureBilling />} />
      <Route path="billing/invoiceorder/success" element={<SuccessBilling />} />
      <Route path="/" element={<Navigate to="/subscription" />} />
      <Route path="*" element={<Navigate to="/subscription" />} />
    </>
  );
}

// کاربر agent بدون subscription
function getAgentRoutesWithExpiredSubscription() {
  return (
    <>
      <Route path="subscription" element={<SubscriptionAgent />} />
      <Route path="/" element={<Navigate to="/subscription" />} />
      <Route path="*" element={<Navigate to="/subscription" />} />
    </>
  );
}

export default App;
